// Dagre ranks by `children` only; `next` targets are dropped below their sources afterwards.

import dagre from "@dagrejs/dagre";
import { DEFAULT_NODE_SIZE, PUSH_GAP, edgeName, findNodeById, buildEdges } from "./treeGraph";

export function layoutGraph(graph, sizes) {
  const g = new dagre.graphlib.Graph({ multigraph: true });
  g.setGraph({ rankdir: "TB", nodesep: 120, ranksep: 140, marginx: 40, marginy: 40 });
  g.setDefaultEdgeLabel(() => ({}));

  graph.nodes.forEach(entry => {
    const size = sizes[entry.id] || DEFAULT_NODE_SIZE;
    g.setNode(entry.id, { width: Math.max(1, size.width), height: Math.max(1, size.height) });
  });

  graph.edges
    .filter(edge => edge.type === "children")
    .forEach(edge => {
      if (g.hasNode(edge.from) && g.hasNode(edge.to)) {
        g.setEdge(edge.from, edge.to, {}, edgeName(edge));
      }
    });

  dagre.layout(g);
  return g;
}

// Dagre centers a rank's nodes on one y; re-align so their tops match instead (shorter nodes move up).
export function topAlignRanks(g, graph) {
  const ranks = new Map();
  graph.nodes.forEach(entry => {
    const gn = g.node(entry.id);
    if (!gn) return;
    const key = Math.round(gn.y);
    if (!ranks.has(key)) ranks.set(key, []);
    ranks.get(key).push(gn);
  });
  ranks.forEach(list => {
    let rankTop = Infinity;
    list.forEach(gn => {
      rankTop = Math.min(rankTop, gn.y - gn.height / 2);
    });
    list.forEach(gn => {
      gn.y = rankTop + gn.height / 2;
    });
  });
}

export function centerRootHorizontally(g, graph) {
  const meta = g.graph();
  const margin = 40;
  const rootEntry = graph.nodes.find(entry => !entry.parent);
  const root = rootEntry && g.node(rootEntry.id);
  if (!root) return meta.width;

  let minLeft = Infinity;
  let maxRight = -Infinity;
  graph.nodes.forEach(entry => {
    const gn = g.node(entry.id);
    if (!gn) return;
    minLeft = Math.min(minLeft, gn.x - gn.width / 2);
    maxRight = Math.max(maxRight, gn.x + gn.width / 2);
  });

  const half = Math.max(root.x - minLeft, maxRight - root.x);
  const shift = margin + half - root.x;
  if (shift) {
    graph.nodes.forEach(entry => {
      const gn = g.node(entry.id);
      if (gn) gn.x += shift;
    });
  }
  return 2 * half + 2 * margin;
}

// Only ever shifts down, and the target's whole subtree moves with it.
export function pushSharedTargetsBelowSources(g, graph, messageTree) {
  const sourcesByTarget = {};
  graph.edges
    .filter(edge => edge.type === "next")
    .forEach(edge => {
      (sourcesByTarget[edge.to] = sourcesByTarget[edge.to] || []).push(edge.from);
    });

  Object.keys(sourcesByTarget).forEach(targetId => {
    const targetNode = findNodeById(messageTree, targetId);
    const tgn = g.node(targetId);
    if (!targetNode || !tgn) return;

    let maxSourceBottom = -Infinity;
    sourcesByTarget[targetId].forEach(sourceId => {
      const sgn = g.node(sourceId);
      if (sgn) maxSourceBottom = Math.max(maxSourceBottom, sgn.y + sgn.height / 2);
    });
    if (maxSourceBottom === -Infinity) return;

    const delta = maxSourceBottom + PUSH_GAP - (tgn.y - tgn.height / 2);
    if (delta <= 0) return;

    const shift = node => {
      if (!node || typeof node !== "object" || !node.id) return;
      const gn = g.node(node.id);
      if (gn) gn.y += delta;
      if (Array.isArray(node.children)) node.children.forEach(shift);
    };
    shift(targetNode);
  });
}

// Anchor each edge to source bottom-port / target top-port centers (dagre splines ignored).
export function buildLayoutEdges(g, graph) {
  return buildEdges(graph.edges, id => {
    const gn = g.node(id);
    return gn && { cx: gn.x, top: gn.y - gn.height / 2, bottom: gn.y + gn.height / 2 };
  });
}

export function computeTreeLayout(graph, sizes, messageTree) {
  const g = layoutGraph(graph, sizes);
  topAlignRanks(g, graph);
  const canvasWidth = centerRootHorizontally(g, graph);
  pushSharedTargetsBelowSources(g, graph, messageTree);

  const layoutNodes = {};
  graph.nodes.forEach(entry => {
    const gn = g.node(entry.id);
    if (gn) {
      layoutNodes[entry.id] = { x: gn.x - gn.width / 2, y: gn.y - gn.height / 2, w: gn.width, h: gn.height };
    }
  });

  const meta = g.graph();
  // Grow the canvas past dagre's reported height to fit pushed-down subtrees.
  let contentBottom = meta.height || 0;
  Object.keys(layoutNodes).forEach(id => {
    contentBottom = Math.max(contentBottom, layoutNodes[id].y + layoutNodes[id].h + 40);
  });

  return {
    layoutNodes,
    layoutEdges: buildLayoutEdges(g, graph),
    canvasSize: { width: Math.ceil(canvasWidth || meta.width || 0), height: Math.ceil(contentBottom) },
  };
}
